import {
    Puzzle,
    Calculator,
    Boxes,
    Award,
    ScrollText,
    Smile,
    WifiOff,
    ShieldOff,
    Play,
    Sparkles,
    CheckCircle2,
    ArrowRight,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useLanguage, pick } from "@/lib/i18n";
import { HOME, PATHS, PLAY_STORE_URL } from "@/lib/translations";
import { DINO_TREX_URL, DINO_BRACHIO_URL } from "@/lib/brand";

const ICONS = [Puzzle, Calculator, Boxes, Award, ScrollText, Smile, WifiOff, ShieldOff];
const TINTS = [
    "bg-amber-500 text-jungle-900",
    "bg-jungle-700 text-cream-50",
    "bg-coral text-jungle-900",
    "bg-sky text-jungle-900",
    "bg-jungle-500 text-jungle-900",
    "bg-amber-400 text-jungle-900",
    "bg-jungle-900 text-amber-400",
    "bg-fossil text-jungle-900",
];

export default function Home() {
    const { lang } = useLanguage();
    const t = pick(HOME, lang);

    return (
        <>
            {/* HERO */}
            <section className="relative overflow-hidden bg-leafy">
                <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute top-16 left-6 w-28 h-28 rounded-full bg-amber-400/40 blur-2xl" />
                    <div className="absolute bottom-10 right-10 w-40 h-40 rounded-full bg-jungle-500/30 blur-3xl" />
                </div>

                <div className="relative max-w-6xl mx-auto px-5 sm:px-6 pt-16 sm:pt-24 pb-20 sm:pb-28 grid lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-7 animate-fade-up">
                        <span
                            data-testid="hero-eyebrow"
                            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border-2 border-jungle-700/15 text-jungle-700 font-display text-xs sm:text-sm font-semibold shadow-soft"
                        >
                            <Sparkles className="w-3.5 h-3.5" strokeWidth={2.5} />
                            {t.eyebrow}
                        </span>

                        <h1 className="mt-5 font-display font-bold text-jungle-900 leading-[1.02] tracking-tight">
                            <span className="block text-5xl sm:text-6xl lg:text-7xl">{t.title1}</span>
                            <span className="block text-5xl sm:text-6xl lg:text-7xl text-jungle-700">{t.title2}</span>
                            <span className="block text-3xl sm:text-4xl lg:text-5xl text-amber-600 mt-1">
                                {t.title3}
                            </span>
                        </h1>

                        <p className="mt-6 text-base sm:text-lg text-ink-500 leading-relaxed max-w-xl">
                            {t.subtitle}
                        </p>

                        <div className="mt-8 flex flex-wrap items-center gap-4">
                            <a
                                href={PLAY_STORE_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="hero-google-play-btn"
                                className="group inline-flex items-center gap-3 px-6 py-4 rounded-full bg-jungle-900 text-cream-50 font-display font-semibold shadow-playful hover:-translate-y-0.5 hover:shadow-playful-sm transition-all"
                            >
                                <Play className="w-5 h-5 fill-amber-500 text-amber-500" />
                                <span className="text-left leading-tight">
                                    <span className="block text-[10px] uppercase tracking-[0.2em] text-amber-400">
                                        {t.getItOn}
                                    </span>
                                    <span className="block text-base">{t.googlePlay}</span>
                                </span>
                            </a>

                            <Link
                                to={PATHS.contact}
                                data-testid="hero-contact-link"
                                className="inline-flex items-center gap-1 px-5 py-3 rounded-full font-display font-semibold text-jungle-900 hover:text-amber-600 transition-colors"
                            >
                                {lang === "de" ? "Support kontaktieren" : "Contact support"} <ArrowRight className="w-4 h-4" />
                            </Link>
                        </div>
                    </div>

                    <div className="lg:col-span-5 relative">
                        <div className="relative aspect-square max-w-md mx-auto">
                            <div className="absolute inset-6 rounded-[2.5rem] bg-amber-400 rotate-3 shadow-card" />
                            <div className="absolute inset-0 rounded-[2.5rem] overflow-hidden border-4 border-jungle-900 bg-cream-100 shadow-playful">
                                <img
                                    src={DINO_TREX_URL}
                                    alt="Dino Detective Tyrannosaurus Rex"
                                    className="w-full h-full object-contain p-6"
                                    loading="eager"
                                />
                            </div>
                            <div className="absolute -top-3 -right-3 inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-white border-2 border-jungle-900 font-display font-bold text-jungle-900 shadow-playful-sm animate-float">
                                <Calculator className="w-4 h-4" strokeWidth={2.5} /> 7 × 8 = ?
                            </div>
                            <div className="absolute -bottom-4 -left-4 inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-amber-500 border-2 border-jungle-900 font-display font-bold text-jungle-900 shadow-playful-sm">
                                <Puzzle className="w-4 h-4" strokeWidth={2.5} /> 100 puzzles
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* FEATURES */}
            <section className="bg-cream-50 bg-paper">
                <div className="max-w-6xl mx-auto px-5 sm:px-6 py-16 sm:py-24">
                    <div className="max-w-2xl">
                        <h2 className="font-display text-4xl sm:text-5xl font-bold text-jungle-900 leading-tight">
                            {t.featuresTitle}
                        </h2>
                        <p className="mt-4 text-base sm:text-lg text-ink-500 leading-relaxed">
                            {t.featuresSubtitle}
                        </p>
                    </div>

                    <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                        {t.features.map((f, i) => {
                            const Icon = ICONS[i % ICONS.length];
                            const tint = TINTS[i % TINTS.length];
                            return (
                                <div
                                    key={f.title}
                                    data-testid={`feature-card-${i}`}
                                    className="group rounded-[2rem] bg-white border-2 border-jungle-700/10 p-6 shadow-soft hover:shadow-card hover:-translate-y-1 transition-all duration-300"
                                >
                                    <div
                                        className={
                                            "inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-4 shadow-playful-sm transition-transform group-hover:-rotate-6 " +
                                            tint
                                        }
                                    >
                                        <Icon className="w-7 h-7" strokeWidth={2.5} />
                                    </div>
                                    <h3 className="font-display text-lg font-semibold text-jungle-900">
                                        {f.title}
                                    </h3>
                                    <p className="mt-1.5 text-sm text-ink-500 leading-relaxed">
                                        {f.desc}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* PREMIUM */}
            <section className="bg-cream-50">
                <div className="max-w-6xl mx-auto px-5 sm:px-6 py-16 sm:py-20">
                    <div className="relative rounded-[2.5rem] overflow-hidden bg-amber-500 border-2 border-jungle-900 shadow-playful">
                        <div className="absolute -right-12 -top-12 w-64 h-64 rounded-full bg-amber-400/60 blur-3xl pointer-events-none" />
                        <div className="absolute -left-12 -bottom-12 w-64 h-64 rounded-full bg-coral/40 blur-3xl pointer-events-none" />
                        <div className="relative grid lg:grid-cols-12 gap-8 items-center p-7 sm:p-12">
                            <div className="lg:col-span-7">
                                <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-jungle-900 text-amber-400 font-display text-xs uppercase tracking-wider">
                                    Premium
                                </span>
                                <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-jungle-900 leading-tight">
                                    {t.premiumTitle}
                                </h2>
                                <p className="mt-3 text-base sm:text-lg text-jungle-900/85 leading-relaxed max-w-xl">
                                    {t.premiumLead}
                                </p>
                                <ul className="mt-6 grid sm:grid-cols-2 gap-3">
                                    {t.premiumBullets.map((b) => (
                                        <li
                                            key={b}
                                            className="flex items-center gap-2 text-jungle-900 font-semibold"
                                        >
                                            <CheckCircle2 className="w-5 h-5 flex-shrink-0" strokeWidth={2.5} />
                                            <span>{b}</span>
                                        </li>
                                    ))}
                                </ul>
                                <p className="mt-6 text-sm text-jungle-900/70 italic">{t.premiumNote}</p>
                            </div>
                            <div className="lg:col-span-5">
                                <div className="relative aspect-square max-w-sm mx-auto">
                                    <div className="absolute inset-0 rounded-[2rem] overflow-hidden border-4 border-jungle-900 bg-cream-100 -rotate-3 shadow-playful">
                                        <img
                                            src={DINO_BRACHIO_URL}
                                            alt="Dino Detective Brachiosaurus"
                                            className="w-full h-full object-contain p-5"
                                            loading="lazy"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="bg-cream-50">
                <div className="max-w-4xl mx-auto px-5 sm:px-6 pb-16 sm:pb-24 text-center">
                    <h2 className="font-display text-3xl sm:text-4xl font-bold text-jungle-900">
                        {t.ctaTitle}
                    </h2>
                    <p className="mt-3 text-ink-500 max-w-xl mx-auto">{t.ctaBody}</p>
                    <a
                        href={PLAY_STORE_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="home-cta-play"
                        className="inline-flex items-center gap-2 mt-6 px-6 py-3 rounded-full bg-jungle-700 text-cream-50 font-display font-semibold shadow-playful-sm hover:bg-jungle-900 transition-colors"
                    >
                        <Play className="w-4 h-4 fill-amber-400 text-amber-400" />
                        {t.ctaButton}
                    </a>
                </div>
            </section>
        </>
    );
}
